import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BorderAllIcon, CodeIcon, ColumnsIcon, FontBoldIcon, FontItalicIcon, Link2Icon, ListBulletIcon, MinusIcon, PinBottomIcon, PinLeftIcon, PinRightIcon, PinTopIcon, QuoteIcon, RowsIcon, StrikethroughIcon, TableIcon, TrashIcon, } from '@radix-ui/react-icons';
import { Highlight } from '@tiptap/extension-highlight';
import { Link } from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TableKit } from '@tiptap/extension-table';
import { EditorContent, useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { StarterKit } from '@tiptap/starter-kit';
import { clsx } from 'clsx';
import * as React from 'react';
import { createPortal } from 'react-dom';
import * as Tooltip from './Tooltip.js';
import { ProtossTokenNode, TokenValueResolverProvider, TokenOverrideProvider, tokenizeTiptapDoc, } from './tokenNode.js';
import { SowDataTableNode, SowDataTableRendererProvider, } from './sowDataTableNode.js';
import { SowAiSectionNode } from './sowAiSectionNode.js';
import { EMPTY_SECTION_BANDS, GeneratedSectionBands, } from './generatedSectionBands.js';
import { LockHeadings } from './lockHeadings.js';
import { HeadingKeys } from './headingKeys.js';
import { HeadingCollapse } from './headingCollapse.js';
import { ScopeChipNode } from './scopeChipNode.js';
import { PropertyListNode, PropertyRowNode, ScopePropertyProvider, } from './scopePropertyNode.js';
import { HeadingDragHandles } from './headingDragHandles.js';
import { HeadingRemoveButtons } from './headingRemoveButtons.js';
// ── Markdown paste utilities ─────────────────────────────────────────────────
function escapeHtml(str) {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
function inlineMd(text) {
    let s = escapeHtml(text);
    s = s.replace(/`([^`\n]+)`/g, '<code>$1</code>');
    s = s.replace(/\*\*\*([^*\n]+)\*\*\*/g, '<strong><em>$1</em></strong>');
    s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/__([^_\n]+)__/g, '<strong>$1</strong>');
    s = s.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
    s = s.replace(/_([^_\n]+)_/g, '<em>$1</em>');
    s = s.replace(/~~([^~\n]+)~~/g, '<s>$1</s>');
    return s;
}
function splitMarkdownTableRow(line) {
    const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
    return trimmed.split('|').map((cell) => cell.trim());
}
function isMarkdownTableSeparator(line) {
    const cells = splitMarkdownTableRow(line);
    return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}
function isMarkdownTableStart(lines, index) {
    if (index + 1 >= lines.length)
        return false;
    const header = lines[index]?.trim() ?? '';
    const separator = lines[index + 1]?.trim() ?? '';
    return header.includes('|') && isMarkdownTableSeparator(separator);
}
function getCodeFenceMarker(line) {
    const trimmed = line.trimStart();
    if (trimmed.startsWith('```'))
        return '```';
    if (trimmed.startsWith('~~~'))
        return '~~~';
    return null;
}
function markdownToHtml(md) {
    const lines = md.split('\n');
    const out = [];
    let inCode = false;
    let codeLines = [];
    let codeFenceMarker = null;
    let listType = null;
    let listHtml = '';
    const flushList = () => {
        if (!listType)
            return;
        out.push(`<${listType}>${listHtml}</${listType}>`);
        listType = null;
        listHtml = '';
    };
    for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index] ?? '';
        const fenceMarker = getCodeFenceMarker(line);
        if (fenceMarker && (!inCode || fenceMarker === codeFenceMarker)) {
            if (!inCode) {
                flushList();
                inCode = true;
                codeFenceMarker = fenceMarker;
                codeLines = [];
            }
            else {
                out.push(`<pre><code>${codeLines.map(escapeHtml).join('\n')}</code></pre>`);
                inCode = false;
                codeFenceMarker = null;
            }
            continue;
        }
        if (inCode) {
            codeLines.push(line);
            continue;
        }
        if (isMarkdownTableStart(lines, index)) {
            flushList();
            const headers = splitMarkdownTableRow(lines[index] ?? '');
            const rows = [];
            index += 2;
            while (index < lines.length) {
                const rowLine = lines[index] ?? '';
                if (!rowLine.trim() || !rowLine.includes('|')) {
                    index -= 1;
                    break;
                }
                rows.push(splitMarkdownTableRow(rowLine));
                index += 1;
            }
            if (index >= lines.length) {
                index -= 1;
            }
            const thead = `<thead><tr>${headers
                .map((cell) => `<th>${inlineMd(cell)}</th>`)
                .join('')}</tr></thead>`;
            const tbody = rows.length
                ? `<tbody>${rows
                    .map((row) => `<tr>${headers
                    .map((_, columnIndex) => `<td>${inlineMd(row[columnIndex] ?? '')}</td>`)
                    .join('')}</tr>`)
                    .join('')}</tbody>`
                : '<tbody></tbody>';
            out.push(`<table>${thead}${tbody}</table>`);
            continue;
        }
        if (/^[-*_]{3,}\s*$/.test(line)) {
            flushList();
            out.push('<hr>');
            continue;
        }
        const hm = line.match(/^(#{1,3})\s+(.*)/);
        if (hm) {
            flushList();
            out.push(`<h${hm[1].length}>${inlineMd(hm[2] ?? '')}</h${hm[1].length}>`);
            continue;
        }
        if (/^> ?/.test(line)) {
            flushList();
            out.push(`<blockquote><p>${inlineMd(line.replace(/^> ?/, ''))}</p></blockquote>`);
            continue;
        }
        const ul = line.match(/^[-*+] (.*)/);
        if (ul) {
            if (listType === 'ol')
                flushList();
            listType = 'ul';
            listHtml += `<li>${inlineMd(ul[1] ?? '')}</li>`;
            continue;
        }
        const ol = line.match(/^\d+\. (.*)/);
        if (ol) {
            if (listType === 'ul')
                flushList();
            listType = 'ol';
            listHtml += `<li>${inlineMd(ol[1] ?? '')}</li>`;
            continue;
        }
        if (!line.trim()) {
            flushList();
            continue;
        }
        flushList();
        out.push(`<p>${inlineMd(line)}</p>`);
    }
    flushList();
    if (inCode)
        out.push(`<pre><code>${codeLines.map(escapeHtml).join('\n')}</code></pre>`);
    return out.join('');
}
function looksLikeMarkdown(text) {
    const lines = text.split('\n');
    return (/^#{1,6}\s/m.test(text) ||
        /^[-*+]\s/m.test(text) ||
        /^\d+\.\s/m.test(text) ||
        /^>\s?/m.test(text) ||
        /^\s*(```|~~~)/m.test(text) ||
        lines.some((_, index) => isMarkdownTableStart(lines, index)) ||
        /\*\*[^*]+\*\*/.test(text) ||
        /__[^_]+__/.test(text) ||
        /~~[^~]+~~/.test(text) ||
        /`[^`\n]+`/.test(text));
}
/** Ids of the built-in slash commands, for hosts building an `allow` list. */
export const SLASH_COMMAND_IDS = [
    'h1',
    'h2',
    'h3',
    'bullet',
    'ordered',
    'quote',
    'table',
    'code',
    'divider',
];
const SLASH_COMMANDS = [
    {
        id: 'h1',
        label: 'Heading 1',
        description: 'Large section header',
        keywords: ['h1', 'heading', 'title'],
        action: (e) => e.chain().focus().setHeading({ level: 1 }).run(),
    },
    {
        id: 'h2',
        label: 'Heading 2',
        description: 'Medium section header',
        keywords: ['h2', 'heading', 'subtitle'],
        action: (e) => e.chain().focus().setHeading({ level: 2 }).run(),
    },
    {
        id: 'h3',
        label: 'Heading 3',
        description: 'Small section header',
        keywords: ['h3', 'heading'],
        action: (e) => e.chain().focus().setHeading({ level: 3 }).run(),
    },
    {
        id: 'bullet',
        label: 'Bullet List',
        description: 'Unordered list',
        keywords: ['ul', 'list', 'bullet', 'unordered'],
        action: (e) => e.chain().focus().toggleBulletList().run(),
    },
    {
        id: 'ordered',
        label: 'Numbered List',
        description: 'Ordered list',
        keywords: ['ol', 'list', 'numbered', 'ordered'],
        action: (e) => e.chain().focus().toggleOrderedList().run(),
    },
    {
        id: 'quote',
        label: 'Quote',
        description: 'Block quote',
        keywords: ['blockquote', 'quote', 'callout'],
        action: (e) => e.chain().focus().toggleBlockquote().run(),
    },
    {
        id: 'table',
        label: 'Table',
        description: 'Pick rows x columns, then insert',
        keywords: ['table', 'grid', 'rows', 'columns', 'markdown table'],
        // Handled specially in executeSlashCommand: opens the size picker at the caret.
        action: (e) => e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
        opensPicker: true,
    },
    {
        id: 'code',
        label: 'Code Block',
        description: 'Multi-line code snippet',
        keywords: ['code', 'code block', 'codeblock', 'pre', 'snippet', 'fence'],
        action: (e) => e.chain().focus().toggleCodeBlock().run(),
    },
    {
        id: 'divider',
        label: 'Divider',
        description: 'Horizontal separator line',
        keywords: ['hr', 'rule', 'divider', 'line', 'separator'],
        action: (e) => e.chain().focus().setHorizontalRule().run(),
    },
];
function filterCommands(commands, query) {
    if (!query)
        return commands;
    const q = query.toLowerCase();
    return commands.filter((cmd) => cmd.label.toLowerCase().includes(q) || cmd.keywords.some((k) => k.startsWith(q)));
}
function SlashMenuPopup({ items, selectedIndex, coords, onSelect, }) {
    const listRef = React.useRef(null);
    // Smart vertical placement: open below the caret, but flip above when the menu would
    // overflow the viewport bottom (and there's room above). Measure after layout; render
    // hidden on the first pass so it never flashes in the wrong spot.
    const [placed, setPlaced] = React.useState({
        top: coords.bottom + 4,
        ready: false,
    });
    React.useLayoutEffect(() => {
        const el = listRef.current;
        if (!el)
            return;
        const h = el.offsetHeight;
        const margin = 8;
        const overflowsBelow = coords.bottom + 4 + h > window.innerHeight - margin;
        const roomAbove = coords.top - margin >= h;
        const top = overflowsBelow && roomAbove ? coords.top - 4 - h : coords.bottom + 4;
        setPlaced({ top, ready: true });
    }, [coords.top, coords.bottom, items.length]);
    // Keep selected item scrolled into view
    React.useEffect(() => {
        const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
        el?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);
    if (items.length === 0)
        return null;
    return (_jsx("div", { className: "rte__slash-menu dropdown-content", style: {
            position: 'fixed',
            left: coords.left,
            top: placed.top,
            visibility: placed.ready ? 'visible' : 'hidden',
        }, ref: listRef, 
        // Prevent clicks inside from closing the menu or blurring the editor
        onMouseDown: (e) => {
            e.preventDefault();
            e.stopPropagation();
        }, children: items.map((cmd, i) => (_jsxs("button", { type: "button", className: clsx('dropdown-item rte__slash-item', i === selectedIndex && 'rte__slash-item--active'), "data-index": i, onMouseDown: (e) => {
                e.preventDefault();
                onSelect(cmd);
            }, children: [_jsx("span", { className: "rte__slash-item-label", children: cmd.label }), _jsx("span", { className: "rte__slash-item-desc", children: cmd.description })] }, cmd.id))) }));
}
// ── Table size picker ───────────────────────────────────────────────────────
const TABLE_PICKER_MAX_ROWS = 8;
const TABLE_PICKER_MAX_COLS = 8;
/**
 * A Notion-style grid-hover picker for choosing a new table's size. Hover to highlight
 * an R x C selection, click to insert. Floats fixed at `coords` (same approach as the
 * slash menu) and dismisses on outside click / Escape.
 */
function TableSizePicker({ coords, onPick, onClose, }) {
    const [hover, setHover] = React.useState({ rows: 0, cols: 0 });
    // Click outside / Escape dismiss (mirrors the slash menu's dismiss effects).
    React.useEffect(() => {
        const onDocMouseDown = () => onClose();
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onClose();
            }
        };
        // Attach the outside-click listener on the NEXT tick. The very mousedown that
        // opened the picker (e.g. the toolbar button, which does not stopPropagation)
        // bubbles to document; attaching synchronously would let that same event
        // immediately close the picker. React flushes this effect within the opening
        // event in real browsers (jsdom does not - hence this is browser-only).
        const attachId = setTimeout(() => {
            document.addEventListener('mousedown', onDocMouseDown);
        }, 0);
        document.addEventListener('keydown', onKeyDown, { capture: true });
        return () => {
            clearTimeout(attachId);
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown, { capture: true });
        };
    }, [onClose]);
    const rows = Array.from({ length: TABLE_PICKER_MAX_ROWS }, (_, i) => i + 1);
    const cols = Array.from({ length: TABLE_PICKER_MAX_COLS }, (_, i) => i + 1);
    return (_jsxs("div", { className: "rte__table-picker dropdown-content", style: { position: 'fixed', left: coords.left, top: coords.top }, 
        // Keep clicks inside from blurring the editor or closing via the document handler
        onMouseDown: (e) => {
            e.preventDefault();
            e.stopPropagation();
        }, children: [_jsx("div", { className: "rte__table-picker-grid", onMouseLeave: () => setHover({ rows: 0, cols: 0 }), children: rows.map((r) => cols.map((c) => (_jsx("button", { type: "button", className: clsx('rte__table-picker-cell', r <= hover.rows && c <= hover.cols && 'rte__table-picker-cell--active'), onMouseEnter: () => setHover({ rows: r, cols: c }), onMouseDown: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onPick(r, c);
                    }, "aria-label": `${r} by ${c} table` }, `${r}-${c}`)))) }), _jsx("div", { className: "rte__table-picker-label", "aria-hidden": "true", children: hover.rows > 0 ? `${hover.rows} x ${hover.cols}` : 'Insert table' })] }));
}
// ── Table bubble menu (controls for an existing table) ──────────────────────
/**
 * Table controls for the current table. Rendered via Tiptap's BubbleMenu (so it tracks
 * the table across scroll/resize) as a small collapsed handle that stays out of the way;
 * clicking the handle expands the add/remove row & column and delete-table controls.
 */
function TableBubbleMenu({ editor }) {
    const [expanded, setExpanded] = React.useState(false);
    const inTable = editor.isActive('table');
    // Collapse back to the handle whenever the caret leaves the table.
    React.useEffect(() => {
        if (!inTable)
            setExpanded(false);
    }, [inTable]);
    // While expanded, collapse on outside-click / Escape. Defer attaching the mousedown
    // listener to the next tick so the click that expanded it cannot immediately collapse
    // it (same browser-only event-timing concern as the size picker).
    React.useEffect(() => {
        if (!expanded)
            return;
        const onDocMouseDown = () => setExpanded(false);
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setExpanded(false);
            }
        };
        const attachId = setTimeout(() => document.addEventListener('mousedown', onDocMouseDown), 0);
        document.addEventListener('keydown', onKeyDown, { capture: true });
        return () => {
            clearTimeout(attachId);
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown, { capture: true });
        };
    }, [expanded]);
    const ctrl = (label, icon, run, can, danger = false) => (_jsx(Tooltip.Simple, { label: label, children: _jsx("button", { type: "button", className: clsx('rte__table-menu-btn', danger && 'rte__table-menu-btn--danger'), onMouseDown: (e) => {
                e.preventDefault();
                e.stopPropagation();
                run();
            }, disabled: !can(), "aria-label": label, children: icon }) }));
    // Distinct delete glyphs: the column/row shape with a "remove" minus badge.
    const deleteColIcon = (_jsxs("span", { className: "rte__table-menu-glyph", "aria-hidden": "true", children: [_jsx(ColumnsIcon, { className: "rte__toolbar-icon" }), _jsx(MinusIcon, { className: "rte__table-menu-affix" })] }));
    const deleteRowIcon = (_jsxs("span", { className: "rte__table-menu-glyph", "aria-hidden": "true", children: [_jsx(RowsIcon, { className: "rte__toolbar-icon" }), _jsx(MinusIcon, { className: "rte__table-menu-affix" })] }));
    return (_jsx(BubbleMenu, { editor: editor, pluginKey: "tableBubbleMenu", shouldShow: ({ editor: e }) => e.isEditable && e.isActive('table'), options: { placement: 'top-start', offset: 6 }, className: "rte__table-menu-wrap", children: expanded ? (_jsxs("div", { className: "rte__table-menu dropdown-content", role: "toolbar", "aria-label": "Table", children: [ctrl('Insert column left', _jsx(PinLeftIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }), () => editor.chain().focus().addColumnBefore().run(), () => editor.can().addColumnBefore()), ctrl('Insert column right', _jsx(PinRightIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }), () => editor.chain().focus().addColumnAfter().run(), () => editor.can().addColumnAfter()), ctrl('Delete column', deleteColIcon, () => editor.chain().focus().deleteColumn().run(), () => editor.can().deleteColumn()), _jsx("div", { className: "rte__table-menu-sep", "aria-hidden": "true" }), ctrl('Insert row above', _jsx(PinTopIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }), () => editor.chain().focus().addRowBefore().run(), () => editor.can().addRowBefore()), ctrl('Insert row below', _jsx(PinBottomIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }), () => editor.chain().focus().addRowAfter().run(), () => editor.can().addRowAfter()), ctrl('Delete row', deleteRowIcon, () => editor.chain().focus().deleteRow().run(), () => editor.can().deleteRow()), _jsx("div", { className: "rte__table-menu-sep", "aria-hidden": "true" }), ctrl('Delete table', _jsx(TrashIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }), () => editor.chain().focus().deleteTable().run(), () => editor.can().deleteTable(), true)] })) : (_jsx(Tooltip.Simple, { label: "Table options", children: _jsx("button", { type: "button", className: "rte__table-handle", onMouseDown: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setExpanded(true);
                }, "aria-label": "Table options", children: _jsx(BorderAllIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }) }) })) }));
}
/** A highlighter-marker glyph (radix has no highlighter icon): a diagonal nib + a stroke. */
function HighlighterIcon({ className }) {
    return (_jsxs("svg", { width: "15", height: "15", viewBox: "0 0 15 15", fill: "none", className: className, "aria-hidden": "true", children: [_jsx("path", { d: "M9.6 1.9 13.1 5.4 7 11.5 3.5 11.5 3.5 8Z", fill: "currentColor", fillOpacity: "0.9" }), _jsx("path", { d: "M2.6 13.4H7.4", stroke: "currentColor", strokeWidth: "1.3", strokeLinecap: "round" })] }));
}
function RteToolbar({ editor, onInsertTable, }) {
    const isH = (level) => editor.isActive('heading', { level });
    return (_jsxs("div", { className: "rte__toolbar", role: "toolbar", "aria-label": "Text formatting", children: [_jsx("button", { type: "button", className: clsx('rte__toolbar-btn', isH(1) && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleHeading({ level: 1 }).run();
                }, "aria-label": "Heading 1", "aria-pressed": isH(1), children: "H1" }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', isH(2) && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleHeading({ level: 2 }).run();
                }, "aria-label": "Heading 2", "aria-pressed": isH(2), children: "H2" }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', isH(3) && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleHeading({ level: 3 }).run();
                }, "aria-label": "Heading 3", "aria-pressed": isH(3), children: "H3" }), _jsx("div", { className: "rte__toolbar-sep", "aria-hidden": "true" }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', editor.isActive('bold') && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleBold().run();
                }, "aria-label": "Bold", "aria-pressed": editor.isActive('bold'), children: _jsx(FontBoldIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }) }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', editor.isActive('italic') && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleItalic().run();
                }, "aria-label": "Italic", "aria-pressed": editor.isActive('italic'), children: _jsx(FontItalicIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }) }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', editor.isActive('strike') && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleStrike().run();
                }, "aria-label": "Strikethrough", "aria-pressed": editor.isActive('strike'), children: _jsx(StrikethroughIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }) }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', editor.isActive('code') && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleCode().run();
                }, "aria-label": "Inline code", "aria-pressed": editor.isActive('code'), children: _jsx(CodeIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }) }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', editor.isActive('highlight') && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleHighlight().run();
                }, "aria-label": "Highlight", "aria-pressed": editor.isActive('highlight'), children: _jsx(HighlighterIcon, { className: "rte__toolbar-icon" }) }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', editor.isActive('link') && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    // Add / edit / remove a hyperlink on the selection. Pre-fill with the existing href when
                    // editing a link; an empty value removes it. Round-trips through markdown [text](url).
                    const prev = editor.getAttributes('link').href ?? 'https://';
                    const url = window.prompt('Link URL (leave blank to remove)', prev);
                    if (url === null)
                        return; // cancelled
                    const href = url.trim();
                    if (!href) {
                        editor.chain().focus().extendMarkRange('link').unsetLink().run();
                        return;
                    }
                    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
                }, "aria-label": "Link", "aria-pressed": editor.isActive('link'), children: _jsx(Link2Icon, { className: "rte__toolbar-icon", "aria-hidden": "true" }) }), _jsx("div", { className: "rte__toolbar-sep", "aria-hidden": "true" }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', editor.isActive('bulletList') && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleBulletList().run();
                }, "aria-label": "Bullet list", "aria-pressed": editor.isActive('bulletList'), children: _jsx(ListBulletIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }) }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', editor.isActive('orderedList') && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleOrderedList().run();
                }, "aria-label": "Ordered list", "aria-pressed": editor.isActive('orderedList'), children: _jsx("span", { "aria-hidden": "true", className: "rte__toolbar-text", children: "1." }) }), _jsx("div", { className: "rte__toolbar-sep", "aria-hidden": "true" }), _jsx("button", { type: "button", className: "rte__toolbar-btn", onMouseDown: (e) => {
                    e.preventDefault();
                    // stopPropagation so this opening click does not bubble to the picker's
                    // outside-click listener (mirrors the slash-menu items).
                    e.stopPropagation();
                    // Anchor the size picker under the button (always on-screen). Anchoring at the
                    // caret like /table can land off-screen in a long, internally-scrolled editor.
                    onInsertTable(e.currentTarget.getBoundingClientRect());
                }, "aria-label": "Insert table", children: _jsx(TableIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }) }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', editor.isActive('codeBlock') && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleCodeBlock().run();
                }, "aria-label": "Code block", "aria-pressed": editor.isActive('codeBlock'), children: _jsx("span", { "aria-hidden": "true", className: "rte__toolbar-text", children: '</>' }) }), _jsx("button", { type: "button", className: clsx('rte__toolbar-btn', editor.isActive('blockquote') && 'rte__toolbar-btn--active'), onMouseDown: (e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleBlockquote().run();
                }, "aria-label": "Blockquote", "aria-pressed": editor.isActive('blockquote'), children: _jsx(QuoteIcon, { className: "rte__toolbar-icon", "aria-hidden": "true" }) })] }));
}
// ── RichTextEditor ─────────────────────────────────────────────────────────
export const RichTextEditor = React.forwardRef(({ content, onUpdate, placeholder = 'Type / to view controls', readOnly = false, className, minHeight, toolbar = false, lineNumbers = false, tokens = false, onEditorReady, brandColor, renderDataTable, resolveToken, onTokenOverride, sectionBands, onToggleSectionDeleted, headingOverride, onResetDataOverride, getAiOriginalContent, lockedHeadings, headingDragHandles, headingDragContainers, collapsibleHeadings, scopeProperty, customizeSlashMenu, headingRemoveButtons, onHeadingRemove, }, ref) => {
    // Tokenize `{{protoss:...}}` text into nodes when either chip mode (`tokens`) or the
    // resolver (deal SOW editor, shows resolved values) is on.
    const enableTokens = tokens || !!resolveToken;
    // Live band data for the GeneratedSectionBands plugin (read via a stable ref so the
    // once-created plugin always sees the latest; a transaction is dispatched on change).
    const sectionBandsRef = React.useRef(sectionBands ?? EMPTY_SECTION_BANDS);
    sectionBandsRef.current = sectionBands ?? EMPTY_SECTION_BANDS;
    // Live delete/restore handler for the inline section control (read via a stable ref).
    const toggleDeleteRef = React.useRef(onToggleSectionDeleted ?? null);
    toggleDeleteRef.current = onToggleSectionDeleted ?? null;
    // Live per-heading data-override lookup + reset handler (heading "Updated" annotation).
    const headingOverrideRef = React.useRef(headingOverride ?? null);
    headingOverrideRef.current = headingOverride ?? null;
    const resetDataRef = React.useRef(onResetDataOverride ?? null);
    resetDataRef.current = onResetDataOverride ?? null;
    // Live AI-section original-content getter + the revert handler (set after the editor exists).
    const aiOriginalRef = React.useRef(getAiOriginalContent ?? null);
    aiOriginalRef.current = getAiOriginalContent ?? null;
    const aiRevertRef = React.useRef(null);
    // Live set of locked (read-only) heading texts for the LockHeadings transaction filter.
    const lockedHeadingsRef = React.useRef(new Set(lockedHeadings ?? []));
    lockedHeadingsRef.current = new Set(lockedHeadings ?? []);
    // Live drag-handle + drop-container predicates for HeadingDragHandles (null = no grips).
    const dragHandlesRef = React.useRef(headingDragHandles ?? null);
    dragHandlesRef.current = headingDragHandles ?? null;
    // Live slash-menu customizer (context scoping + host extra commands); null = full built-ins.
    const customizeSlashMenuRef = React.useRef(customizeSlashMenu ?? null);
    customizeSlashMenuRef.current = customizeSlashMenu ?? null;
    const dragContainersRef = React.useRef(headingDragContainers ?? null);
    dragContainersRef.current = headingDragContainers ?? null;
    // Live master switch for HeadingCollapse (read through a ref so the once-created plugin
    // always sees the latest; collapse is inert until true).
    const collapsibleHeadingsRef = React.useRef(collapsibleHeadings ?? false);
    collapsibleHeadingsRef.current = collapsibleHeadings ?? false;
    // Live remove-button predicate + handler for HeadingRemoveButtons (null = no x buttons).
    const removeButtonsRef = React.useRef(headingRemoveButtons ?? null);
    removeButtonsRef.current = headingRemoveButtons ?? null;
    const onHeadingRemoveRef = React.useRef(onHeadingRemove ?? null);
    onHeadingRemoveRef.current = onHeadingRemove ?? null;
    // Force re-render on selection change so toolbar active states stay accurate
    const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
    // ── Slash command state ──────────────────────────────────────────────────
    const [slashMenu, setSlashMenu] = React.useState(null);
    const [slashSelectedIndex, setSlashSelectedIndex] = React.useState(0);
    // ── Table size picker (new-table insertion) ──────────────────────────────
    const [tablePicker, setTablePicker] = React.useState(null);
    // Refs give the keydown handler immediate access without stale closures
    const slashMenuRef = React.useRef(null);
    const slashIndexRef = React.useRef(0);
    slashMenuRef.current = slashMenu;
    slashIndexRef.current = slashSelectedIndex;
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                // StarterKit v3 bundles its own Link extension; disable it so the
                // explicitly-configured Link below is the only one (avoids tiptap's
                // "Duplicate extension names found: ['link']" warning).
                link: false,
            }),
            TableKit.configure({
                table: { resizable: false },
            }),
            Placeholder.configure({ placeholder }),
            Link.configure({
                openOnClick: readOnly,
                autolink: true,
                HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
            }),
            // User text highlight (single color today; the mark + `TextRunDefinition.highlight`
            // carry a color name so this stays extensible to more colors later). Registered
            // unconditionally so loaded/pasted `<mark>` content never throws "Unknown mark".
            // `rte-hl-user` styles it to the distinct user-highlight shade (vs the auto yellow).
            Highlight.configure({ HTMLAttributes: { class: 'rte-hl-user' } }),
            // Register both custom nodes UNCONDITIONALLY so the schema can always parse them
            // regardless of when the async props that drive them (`resolveToken` /
            // `renderDataTable`) arrive (the editor's schema is frozen at mount). Whether a
            // doc actually contains these nodes is controlled separately - `protossToken`
            // nodes only appear when `enableTokens` tokenizes the content, and `sowDataTable`
            // nodes only when the host injects them - so an always-present node is inert when
            // unused (its view renders the raw chip / null without its provider). Gating
            // registration on an async prop caused a blank editor:
            // setContent threw "Unknown node type: sowDataTable" when the node was missing.
            ProtossTokenNode,
            SowDataTableNode,
            SowAiSectionNode,
            ScopeChipNode,
            // Notion-style property-panel nodes (deal Scope doc). Registered unconditionally like the
            // nodes above so the schema always parses them; inert (rows render nothing) until the host
            // wires `scopeProperty`.
            PropertyListNode,
            PropertyRowNode,
            // Heading `data-key` model anchors + the host-gated :: drag grips (deal Scope doc).
            // Registered unconditionally like the nodes above; inert when the host never sets them.
            HeadingKeys,
            HeadingDragHandles.configure({
                getEnabled: () => dragHandlesRef.current,
                getContainers: () => dragContainersRef.current,
            }),
            GeneratedSectionBands.configure({
                getBands: () => sectionBandsRef.current,
                getOnToggleDelete: () => toggleDeleteRef.current,
                getHeadingOverride: () => headingOverrideRef.current,
                getOnResetData: () => resetDataRef.current,
                getOnRevertAi: () => aiRevertRef.current,
            }),
            LockHeadings.configure({ getLocked: () => lockedHeadingsRef.current }),
            HeadingCollapse.configure({ getEnabled: () => collapsibleHeadingsRef.current }),
            HeadingRemoveButtons.configure({
                getEnabled: () => removeButtonsRef.current,
                getOnRemove: () => onHeadingRemoveRef.current,
            }),
        ],
        content: (enableTokens ? tokenizeTiptapDoc(content ?? null) : content) ?? undefined,
        editable: !readOnly,
        // Defer the editor's first render (and its React node-view mounts) to a post-mount
        // effect instead of doing it inline during the host's render/commit. Tiptap's node-view
        // creation calls flushSync; rendering immediately makes that re-entrant when the editor
        // mounts inside a React-Router `startTransition` commit (View->Edit nav), firing the
        // "flushSync from inside a lifecycle method" warning. (Tiptap v3's recommended setting.)
        immediatelyRender: false,
        onSelectionUpdate: () => forceUpdate(),
        onUpdate: ({ editor: e }) => {
            forceUpdate();
            onUpdate?.(e.getJSON(), e.getText());
        },
    });
    // Revert an edited AI section to its original generated content: find the `sowAiSection` node
    // by slug and replace its content with the host-supplied original blocks (re-tokenized so any
    // `{{...}}` render as chips). Kept in a ref so GeneratedSectionBands' once-created plugin can
    // call the latest. Null when no original-content getter is wired (the badge isn't shown).
    aiRevertRef.current = aiOriginalRef.current
        ? (slug) => {
            // Throw (rather than silently return) on every failure path so the heading badge's
            // spinner is restored + a retry hint shown (runWithSpinner only reacts to a rejection;
            // a silent return would leave a stuck spinner with no re-decoration).
            if (!editor || editor.isDestroyed)
                throw new Error('editor unavailable');
            const blocks = aiOriginalRef.current?.(slug);
            if (!blocks || blocks.length === 0)
                throw new Error('no original AI content');
            const { state } = editor;
            let target = null;
            state.doc.descendants((node, pos) => {
                if (target)
                    return false;
                if (node.type.name === 'sowAiSection' && String(node.attrs.slug ?? '') === slug) {
                    target = { pos, node };
                    return false;
                }
                return true;
            });
            if (!target)
                throw new Error('AI section not found');
            const prepared = enableTokens
                ? (tokenizeTiptapDoc({ type: 'doc', content: blocks })
                    ?.content ?? blocks)
                : blocks;
            const pmNodes = prepared.map((j) => state.schema.nodeFromJSON(j));
            const t = target;
            const from = t.pos + 1;
            const to = t.pos + t.node.nodeSize - 1;
            editor.view.dispatch(state.tr.replaceWith(from, to, pmNodes));
        }
        : null;
    // Sync external content changes into the editor (e.g., switching documents).
    React.useEffect(() => {
        if (!editor || editor.isDestroyed)
            return;
        const prepared = enableTokens ? tokenizeTiptapDoc(content ?? null) : (content ?? null);
        // Normalize the incoming JSON through the schema before comparing: getJSON() emits every
        // schema attr including defaults (e.g. HeadingKeys' dataKey:null), so a raw-JSON string
        // compare against content that omits defaulted attrs would always mismatch and re-run
        // setContent on each content-identity change. Content the schema cannot parse falls back
        // to the raw compare - setContent is the right outcome for it anyway.
        let incoming;
        try {
            incoming = prepared
                ? JSON.stringify(editor.schema.nodeFromJSON(prepared).toJSON())
                : JSON.stringify(null);
        }
        catch {
            incoming = JSON.stringify(prepared ?? null);
        }
        const current = JSON.stringify(editor.getJSON());
        if (incoming === current)
            return;
        // Defer setContent to a microtask. setContent mounts React node views (token /
        // sowDataTable nodes) via Tiptap's ReactRenderer, which calls flushSync; running
        // it synchronously here triggers React's "flushSync was called from inside a
        // lifecycle method" warning when this effect fires during a commit/passive-effect
        // flush (e.g. when the async SOW render-context resolves and changes `content`).
        // The microtask runs after React leaves that phase. Re-check isDestroyed since the
        // editor can unmount first; `cancelled` drops a stale run if `content` changes again.
        let cancelled = false;
        queueMicrotask(() => {
            if (cancelled || editor.isDestroyed)
                return;
            editor.commands.setContent(prepared ?? '', { emitUpdate: false });
        });
        return () => {
            cancelled = true;
        };
    }, [content, editor, enableTokens]);
    // Re-run the section-band decorations when the band data changes (it loads async with
    // the SOW render context). Dispatching an empty transaction recomputes decorations
    // without changing the doc (no `onUpdate`, since nothing in the document changed).
    // Deferred to a microtask so it never dispatches inside a React commit/passive-effect
    // flush (Tiptap's node-view sync can call flushSync there).
    React.useEffect(() => {
        if (!editor || editor.isDestroyed)
            return;
        let cancelled = false;
        queueMicrotask(() => {
            if (cancelled || editor.isDestroyed)
                return;
            editor.view.dispatch(editor.state.tr);
        });
        return () => {
            cancelled = true;
        };
        // headingOverride changes identity when the data-override state changes, so re-decorate
        // (the heading "Updated" annotation) alongside the band data.
    }, [editor, sectionBands, headingOverride]);
    // Expose the editor instance (and null on unmount) for token insertion.
    React.useEffect(() => {
        if (!editor)
            return;
        onEditorReady?.(editor);
        return () => onEditorReady?.(null);
        // onEditorReady is expected stable; omit to avoid re-registering churn.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);
    // Sync editable flag. Pass emitUpdate=false: toggling editability is not a
    // content change, and Tiptap's setEditable emits an `update` event by default.
    // That spurious update (fired once on mount) would otherwise reach onUpdate and
    // look like a real edit to consumers - e.g. it armed the SOW editor's autosave
    // on open, phantom-saving with zero keystrokes.
    React.useEffect(() => {
        if (!editor || editor.isDestroyed)
            return;
        editor.setEditable(!readOnly, false);
    }, [readOnly, editor]);
    // Markdown paste handler - intercepts plain-text paste and converts markdown to rich nodes
    React.useEffect(() => {
        if (!editor || editor.isDestroyed || readOnly)
            return;
        const dom = editor.view.dom;
        const onPaste = (e) => {
            if (!e.clipboardData)
                return;
            const text = e.clipboardData.getData('text/plain');
            if (!text || !looksLikeMarkdown(text))
                return;
            // Prevent Tiptap (and browser) from handling this paste - we convert ourselves
            e.preventDefault();
            e.stopImmediatePropagation();
            const converted = markdownToHtml(text);
            editor
                .chain()
                .focus()
                .insertContent(converted, { parseOptions: { preserveWhitespace: false } })
                .scrollIntoView()
                .run();
        };
        dom.addEventListener('paste', onPaste, { capture: true });
        return () => dom.removeEventListener('paste', onPaste, { capture: true });
    }, [editor, readOnly]);
    // ── Slash command detection ──────────────────────────────────────────────
    React.useEffect(() => {
        if (!editor || editor.isDestroyed || readOnly)
            return;
        const detect = () => {
            const { $from, from, to } = editor.state.selection;
            if (from !== to) {
                setSlashMenu(null);
                return;
            }
            const textBefore = $from.parent.textContent.slice(0, $from.parentOffset);
            const match = textBefore.match(/(^|\s)(\/\w*)$/);
            if (!match) {
                setSlashMenu(null);
                return;
            }
            const slashText = match[2]; // e.g. "/h1"
            const triggerPos = $from.pos - slashText.length;
            const coords = editor.view.coordsAtPos(triggerPos);
            // Resolve the command set for this menu open: the host customizer scopes the built-ins
            // by cursor context and can append its own commands; no customizer = all built-ins.
            const cfg = customizeSlashMenuRef.current?.({ editor, triggerPos }) ?? null;
            const allowed = cfg?.allow
                ? SLASH_COMMANDS.filter((c) => cfg.allow.includes(c.id))
                : SLASH_COMMANDS;
            const commands = cfg?.extra?.length ? [...allowed, ...cfg.extra] : allowed;
            setSlashMenu({
                query: slashText.slice(1),
                triggerPos,
                commands,
                // Pass the caret rect; the popup positions itself (below by default, flipped above
                // when it would overflow the viewport bottom).
                coords: { left: coords.left, top: coords.top, bottom: coords.bottom },
            });
        };
        editor.on('update', detect);
        editor.on('selectionUpdate', detect);
        return () => {
            editor.off('update', detect);
            editor.off('selectionUpdate', detect);
        };
    }, [editor, readOnly]);
    // Reset selection index when query changes
    React.useEffect(() => {
        setSlashSelectedIndex(0);
    }, [slashMenu?.query]);
    // Open the table size picker at a viewport position, clamped so it is never
    // rendered off-screen (approx picker size used for the clamp).
    const openTablePickerAt = React.useCallback((left, top) => {
        const PICKER_W = 200;
        const PICKER_H = 220;
        const clampedLeft = Math.max(8, Math.min(left, window.innerWidth - PICKER_W));
        const clampedTop = Math.max(8, Math.min(top, window.innerHeight - PICKER_H));
        setSlashMenu(null);
        setSlashSelectedIndex(0);
        setTablePicker({ left: clampedLeft, top: clampedTop });
    }, []);
    // Caret-based open for the /table slash command (the caret is on-screen while typing).
    const openTablePickerAtCaret = React.useCallback(() => {
        if (!editor)
            return;
        const coords = editor.view.coordsAtPos(editor.state.selection.from);
        openTablePickerAt(coords.left, coords.bottom + 4);
    }, [editor, openTablePickerAt]);
    // Execute a slash command: delete trigger text then apply block
    const executeSlashCommand = React.useCallback((cmd) => {
        if (!editor || !slashMenuRef.current)
            return;
        const sm = slashMenuRef.current;
        const { $from } = editor.state.selection;
        editor.chain().focus().deleteRange({ from: sm.triggerPos, to: $from.pos }).run();
        setSlashMenu(null);
        setSlashSelectedIndex(0);
        if (cmd.opensPicker) {
            // Open the size picker at the caret instead of inserting directly.
            openTablePickerAtCaret();
            return;
        }
        cmd.action(editor);
    }, [editor, openTablePickerAtCaret]);
    // Keyboard navigation for slash menu (capture phase so Tiptap doesn't see arrow/enter)
    React.useEffect(() => {
        if (!slashMenu || !editor)
            return;
        const filtered = filterCommands(slashMenu.commands, slashMenu.query);
        const n = filtered.length;
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                e.stopPropagation();
                const next = Math.min(slashIndexRef.current + 1, n - 1);
                slashIndexRef.current = next;
                setSlashSelectedIndex(next);
            }
            else if (e.key === 'ArrowUp') {
                e.preventDefault();
                e.stopPropagation();
                const next = Math.max(slashIndexRef.current - 1, 0);
                slashIndexRef.current = next;
                setSlashSelectedIndex(next);
            }
            else if (e.key === 'Enter') {
                const cmd = filtered[slashIndexRef.current];
                if (cmd) {
                    e.preventDefault();
                    e.stopPropagation();
                    executeSlashCommand(cmd);
                }
            }
            else if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setSlashMenu(null);
                setSlashSelectedIndex(0);
            }
        };
        document.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
    }, [slashMenu, editor, executeSlashCommand]);
    // Click outside to dismiss (leave "/" text intact)
    React.useEffect(() => {
        if (!slashMenu)
            return;
        const handleMouseDown = () => {
            setSlashMenu(null);
            setSlashSelectedIndex(0);
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, [slashMenu]);
    const rootStyle = React.useMemo(() => {
        const vars = {};
        if (minHeight)
            vars['--rte-min-height'] = minHeight;
        if (brandColor) {
            // Inject only the raw brand color; components.css derives theme-aware table
            // fill/text/border from it (mixing toward --text-primary, which flips per
            // color scheme) so the brand stays readable in both light and dark themes.
            vars['--rte-brand'] = brandColor.startsWith('#') ? brandColor : `#${brandColor}`;
        }
        return Object.keys(vars).length > 0 ? vars : undefined;
    }, [minHeight, brandColor]);
    const editorTree = (_jsxs("div", { ref: ref, className: clsx('rte', readOnly && 'rte--read-only', lineNumbers && 'rte--line-numbers', className), style: rootStyle, children: [toolbar && !readOnly && editor && (_jsx(RteToolbar, { editor: editor, onInsertTable: (rect) => openTablePickerAt(rect.left, rect.bottom + 4) })), _jsx(EditorContent, { editor: editor, className: "rte__content" }), editor && !readOnly && _jsx(TableBubbleMenu, { editor: editor }), slashMenu && (_jsx(SlashMenuPopup, { items: filterCommands(slashMenu.commands, slashMenu.query), selectedIndex: slashSelectedIndex, coords: slashMenu.coords, onSelect: executeSlashCommand })), tablePicker &&
                editor &&
                // Portal to <body> so the picker escapes the editor's `overflow: hidden`
                // and any ancestor stacking context - it must always be visible on top.
                createPortal(_jsx(TableSizePicker, { coords: tablePicker, onPick: (rows, cols) => {
                        editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
                        setTablePicker(null);
                    }, onClose: () => setTablePicker(null) }), document.body)] }));
    // Provide the data-table renderer to injected sowDataTable nodes and the token
    // resolver to inline protossToken nodes. Render BOTH providers UNCONDITIONALLY (passing
    // null when unused) so the component tree is structurally stable: these props load async
    // (with the SOW render context), and conditionally adding a wrapper would change the tree
    // shape and REMOUNT the whole editor - losing editor state and firing a flushSync-in-
    // commit warning as Tiptap re-mounts its React node views. A null value just makes the
    // node views render their fallback (chip / nothing) until the real value arrives.
    return (_jsx(SowDataTableRendererProvider, { renderer: renderDataTable ?? null, children: _jsx(TokenValueResolverProvider, { resolve: resolveToken ?? null, children: _jsx(TokenOverrideProvider, { onOverride: onTokenOverride ?? null, children: _jsx(ScopePropertyProvider, { api: scopeProperty ?? null, children: editorTree }) }) }) }));
});
RichTextEditor.displayName = 'RichTextEditor';
//# sourceMappingURL=RichTextEditor.js.map